/**
 * lastFilter — localStorage persistence for the user's most recent filter selection.
 *
 * Key:   orscout.lastFilter
 * Shape: { sport, position, division, region, target_year, savedAt: ISO8601 }
 *
 * Used by SearchPage to support the returning-visitor division-pill fast path:
 *  - Read on division-pill click to detect whether a usable filter exists.
 *  - Written on every filter submission.
 *  - First-time visitors (no localStorage key): returns null; caller uses first-time path.
 *
 * Storage format (compact):
 *  - position: comma-separated string (split on read)
 *  - division: comma-separated string (split on read) — WI-43 multi-select
 */

const STORAGE_KEY = 'orscout.lastFilter';

export interface LastFilter {
  sport?: string;
  /** Array of position slugs. */
  position?: string[];
  /** Array of division slugs (multi-select). WI-43. */
  division?: string[];
  region?: string;
  target_year?: number;
  savedAt: string;
}

/** Stored shape (compact — arrays as comma-separated strings). */
interface StoredLastFilter {
  sport?: string;
  position?: string;
  division?: string;
  region?: string;
  target_year?: number;
  savedAt: string;
}

/**
 * Read the last filter from localStorage.
 * Returns null when:
 *  - No value is stored (first-time visitor).
 *  - The stored value is malformed JSON.
 *  - The sport field is absent (incomplete filter — empty state).
 */
export function readLastFilter(): LastFilter | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredLastFilter = JSON.parse(raw);
    if (!stored.sport) return null;
    return {
      sport: stored.sport,
      position: stored.position
        ? stored.position.split(',').filter(Boolean)
        : undefined,
      division: stored.division
        ? stored.division.split(',').filter(Boolean)
        : undefined,
      region: stored.region,
      target_year: stored.target_year,
      savedAt: stored.savedAt ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Write the current filter to localStorage.
 * Silently ignores write errors (private browsing, storage quota, etc.).
 *
 * Accepts either SearchFilters (division: string[]) or the legacy shape
 * (division: string) for backward compatibility with existing callers.
 */
export function writeLastFilter(filter: {
  sport?: string;
  position?: string[];
  division?: string | string[];
  region?: string;
  target_year?: number;
}): void {
  try {
    // Normalize division to comma-separated string for compact storage.
    let divisionStr: string | undefined;
    if (Array.isArray(filter.division)) {
      divisionStr = filter.division.length > 0 ? filter.division.join(',') : undefined;
    } else if (typeof filter.division === 'string' && filter.division.length > 0) {
      divisionStr = filter.division;
    }

    const stored: StoredLastFilter = {
      sport: filter.sport,
      position: filter.position && filter.position.length > 0
        ? filter.position.join(',')
        : undefined,
      division: divisionStr,
      region: filter.region,
      target_year: filter.target_year,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Silently ignore — private browsing mode or quota exceeded.
  }
}

/**
 * Clear the stored filter (e.g., when the user explicitly resets all filters).
 * Silently ignores errors.
 */
export function clearLastFilter(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently ignore.
  }
}
